/**
 * Shared utility functions for message display
 */

/**
 * Format a timestamp into a localized date string
 * @param {string|Date} timestamp - The date/time to format
 * @param {boolean} long - Whether to use long month format
 * @returns {string} Formatted date string
 */
export function formatDate(timestamp, long = false) {
  if (!timestamp) return "";
  const dateObj = new Date(timestamp);
  if (isNaN(dateObj.getTime())) return "";
  const options = {
    year: "numeric",
    month: long ? "long" : "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return dateObj.toLocaleDateString(undefined, options);
}

/**
 * Generate a consistent HSL color from a string (for avatars)
 * @param {string} str - Input string to hash
 * @returns {string} HSL color string
 */
export function stringToColor(str) {
  if (!str) return "#6fa0c7";
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 60%)`;
}

const RTL_CHARS = /[֐-׿؀-ۿ܀-ݏ]/;

/**
 * Detect the writing direction of a piece of text (Hebrew/Arabic → rtl)
 * @param {string} text - The text to inspect
 * @returns {"rtl"|"ltr"} Direction for the text block
 */
export function getDirection(text) {
  return RTL_CHARS.test(text || "") ? "rtl" : "ltr";
}

const NEW_BADGE_DAYS = 7;

/**
 * Whether a timestamp is recent enough to show a "New" badge
 * @param {string|Date} timestamp - The entry's timestamp
 * @returns {boolean}
 */
export function isRecent(timestamp) {
  if (!timestamp) return false;
  const dateObj = new Date(timestamp);
  if (isNaN(dateObj.getTime())) return false;
  return Date.now() - dateObj.getTime() < NEW_BADGE_DAYS * 24 * 3600 * 1000;
}

/**
 * Normalize a value that may be a string or an object with label/value
 * @param {string|object} value - The value to normalize
 * @returns {string} The normalized string label
 */
export function normalizeLabel(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?.label || value?.value || "";
}
